import {
    useEffect,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IMessageScrollerContentProps from "./type";
import stylesheet, {
    MESSAGE_SCROLLER_SKELETON_ROWS,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useMessageScrollerRootContext,
    useMessageScrollerSelector,
    useMessageScrollerContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";
import Skeleton from "../../../skeleton";
import Marker from "../../../marker";

const MessageScrollerContent: FC<IMessageScrollerContentProps> = ({
    customTheme,
    children,
    isBusy,
    style,
    ...props
}) => {
    const context = useMessageScrollerContext();

    const rootContext = useMessageScrollerRootContext();

    const currentTheme = customTheme ?? rootContext?.customTheme;

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(rootContext?.customLocalize);

    const spacerHeight = useMessageScrollerSelector(context, (snapshot) => snapshot.spacerHeight, 0);

    const {
        container: containerDynamicStyle
    } = useStyles({
        stackBehaviour: context?.stackBehaviour ?? "top",
        shapeConfigs,
        spacerHeight,
        spaces
    });

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "MessageScroller.Content: it must be rendered inside a MessageScroller.Provider."
        });
    }, [
        context
    ]);

    const renderStart = () => {
        if(context?.hasOlderMessages !== false || context.isLoading) {
            return null;
        }

        return <Marker
            customTheme={currentTheme}
            variant="separator"
        >
            {localize("start-of-conversation")}
        </Marker>;
    };

    const renderSkeleton = () => {
        return Array.from({
            length: MESSAGE_SCROLLER_SKELETON_ROWS
        }, (_, index) => <Skeleton
            width={index % 2 ? "60%" : "80%"}
            customTheme={currentTheme}
            variant="text"
            key={index}
            lines={2}
        />);
    };

    return <View
        {...props}
        aria-busy={isBusy ?? context?.isLoading ?? false}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-live="polite"
        role="log"
    >
        {renderStart()}
        {context?.isLoading ? renderSkeleton() : children}
    </View>;
};
export default MessageScrollerContent;
